var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Step, StepContent, StepLabel, Stepper } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Grid, Spacer, Typography } from '@emburse/ui-core';
import { signupForEmburseCards } from '@ReactComponents/../utils/emburseCardsSignupHelper';
import { GoToEmburse } from '@ReactComponents/GoToEmburse';
import { CompanyInformationForm } from '@ReactComponents/Onboard/CompanyInformation';
import { ShowModal } from '@Actions/index';
import { useTimeoutManager } from '@ReactHooks/useTimeoutManager';
import { CompanyApi } from '@ClientApi/company';
import { GoogleAnalyticsService, GoogleEvents } from '@ClientApi/google';
import { PendoEvents, PendoService } from '@ClientApi/pendo';
import { PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR } from '@Shared/constants/partners';
import { SignUpConfirmationModal } from './SignUpEmburseCards/SignUpConfirmationModal';
import { SignUpEmburseCards } from './SignUpEmburseCards/SignUpEmburseCards';
import { SignUpImplementationSelection } from './SignUpImplementationSelection/SignUpImplementationSelection';
import { SignUpPaymentSummary } from './SignUpPaymentSummary/SignUpPaymentSummary';
import { SignUpPersonalInfo } from './SignUpPersonalInfo/SignUpPersonalInfo';
import { SignUpPlanSelection } from './SignUpPlanSelection/SignUpPlanSelection';
import { SignUpReferralScreen } from './SignUpReferralScreen/SignUpReferralScreen';
import SignUpReviews from './SignUpReviews/SignUpReviews';
import { SignUpWelcomeScreen } from './SignUpWelcomeScreen/SignUpWelcomeScreen';
import { VerifyEmail } from './VerifyEmail';
import { STAGE, getAvailableStepsData } from './signUpWorkflowSteps';
import { getSignUpChannel, isAmexCompany } from './singup_helper_functions';
import * as style from './SignUpWorkflow.styl';
const PENDO_MAPPING = {
    [STAGE.EMAIL_VERIFICATION]: PendoEvents.SIGNUP_FLOW_EMAIL_VERIFICATION,
    [STAGE.COMPANY_INFORMATION]: PendoEvents.SIGNUP_FLOW_COMPANY_INFO,
    [STAGE.PERSONAL_INFORMATION]: PendoEvents.SIGNUP_FLOW_PERSONAL_INFO,
    [STAGE.PLAN_SELECTION]: PendoEvents.SIGNUP_FLOW_PLAN_SELECTION,
    [STAGE.IMPLEMENTATION_OPTIONS]: PendoEvents.SIGNUP_FLOW_IMPLEMENTATION_OPTIONS,
    [STAGE.EMBURSE_CARDS]: PendoEvents.SIGNUP_FLOW_EMBURSE_CARDS,
    [STAGE.BUSINESS_VERIFICATION]: PendoEvents.SIGNUP_FLOW_BUSINESS_VERIFICATION,
    [STAGE.REFERRAL_SCREEN]: PendoEvents.SIGNUP_FLOW_REFERRAL_SCREEN,
    [STAGE.RSM_PARTNER_REFERRAL_SCREEN]: PendoEvents.SIGNUP_FLOW_REFERRAL_SCREEN,
    [STAGE.SUBMIT_APPLICATION]: PendoEvents.SIGNUP_FLOW_SUBMIT_APPLICATION,
    [STAGE.PAYMENT_SUMMARY]: PendoEvents.SIGNUP_FLOW_PAYMENT_SUMMARY,
    [STAGE.FINISH]: PendoEvents.SIGNUP_FLOW_FINISHED
};
const stages = Object.values(STAGE);
const useSignupCompanyForm = (company) => useForm({
    defaultValues: {
        name: company.name === ' ' ? '' : company.name,
        street_address: company.street_address || '',
        address_city: company.address_city || '',
        postal_code: company.postal_code || '',
        website: company.website || '',
        address_state: {
            code: company.address_country_code || '',
            country_code: company.address_country_code || '',
            subdivision_name: company.address_state || ''
        },
        address_country_code: company.address_country_code || 'USA',
        phone_number: company.phone_number || '',
        employees_with_expenses_range: company.employees_with_expenses_range || ''
    }
});
function getCardsOffer(company, isAdditionalCompany) {
    var _a;
    if (!company) {
        return 'offer';
    }
    if (isAdditionalCompany || isAmexCompany(company)) {
        return 'hide';
    }
    const utm_parameter = company === null || company === void 0 ? void 0 : company.utm_parameter;
    return ((_a = utm_parameter === null || utm_parameter === void 0 ? void 0 : utm_parameter.referralParameters) === null || _a === void 0 ? void 0 : _a.cards) || '';
}
export const SignUpWorkflow = ({ startStage, isAdditionalCompany }) => {
    var _a, _b;
    const { acsUrl, assertionResponse } = window;
    const dispatch = useDispatch();
    const logo = (_b = (_a = window.whitelabelConfiguration) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.appLogoPath;
    const [activeStep, setActiveStep] = React.useState(0);
    const [currentStage, setCurrentStage] = React.useState(STAGE.COMPANY_INFORMATION);
    const [completed, setCompleted] = React.useState({});
    const [applyForEmburseCards, setApplyForEmburseCards] = React.useState(false);
    const [apiError, setApiError] = React.useState(null);
    const company = useSelector((state) => state.company.company);
    const user = useSelector((state) => state.currentUser.user);
    const channel = getSignUpChannel(company);
    const cards = getCardsOffer(company, isAdditionalCompany);
    const isFromSales = channel === 'sales';
    const isRsmPartner = [PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR].includes(channel);
    const skipReferralCards = isAdditionalCompany || isAmexCompany(company) ? false : isFromSales && !(cards === 'offer' || cards === 'force');
    const { steps, stageToStepMapping } = React.useMemo(() => getAvailableStepsData(isFromSales, cards, skipReferralCards, isAdditionalCompany, isRsmPartner), []);
    useTimeoutManager();
    const companyFormMethods = useSignupCompanyForm(company);
    const setGenericStripeError = () => {
        setApiError('There was an issue contacting our payment processor — please try again later');
    };
    const handleConnection = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!(company === null || company === void 0 ? void 0 : company.id)) {
            return;
        }
        const connections = yield CompanyApi.getCompanyGatewayConnections(company.id);
        //Check if company is created in Gateway
        if (connections.length === 0 || (connections.length === 1 && connections[0] === null)) {
            return goToStage(STAGE.COMPANY_INFORMATION);
        }
        // This will actually be the status of the Emburse Cards company, as this response is transformed slightly by our backend
        // This is not the status of the Connection.
        const incompleteCardsApplication = connections.find((x) => (x === null || x === void 0 ? void 0 : x.verification_status) === 'unsubmitted' || (x === null || x === void 0 ? void 0 : x.verification_status) === 'unverified');
        // In case there is more than one application, check if at least one is verified to avoid a loop
        const verifiedCardsApp = connections.some((x) => (x === null || x === void 0 ? void 0 : x.verification_status) === 'verified');
        if (incompleteCardsApplication && !verifiedCardsApp) {
            goToStage(STAGE.BUSINESS_VERIFICATION, true);
        }
        else {
            setApplyForEmburseCards(true);
            if (isFromSales) {
                goToStage(STAGE.FINISH, true);
            }
            else {
                goToStage(STAGE.PAYMENT_SUMMARY, true);
            }
        }
    });
    React.useEffect(() => {
        const isVerified = user.state !== 'unverified';
        const hasInfoFilled = user.has_info_filled;
        if (!isVerified) {
            goToStage(STAGE.EMAIL_VERIFICATION);
        }
        if (hasInfoFilled) {
            handleConnection().catch(() => goToStage(STAGE.COMPANY_INFORMATION));
        }
    }, [user.state]);
    React.useEffect(() => {
        if (isFromSales && startStage === STAGE.PAYMENT_SUMMARY) {
            startStage = STAGE.FINISH;
        }
        let index = stages.findIndex((val) => val === startStage);
        if (index !== -1) {
            goToStage(stages[index], true);
        }
    }, [startStage]);
    function getNextStage(direction) {
        let index = stages.findIndex((val) => val === currentStage);
        if (index === -1) {
            return undefined;
        }
        const delta = direction === 'next' ? 1 : -1;
        while (index >= 0 && index < stages.length) {
            index = index + delta;
            const nextStage = stages[index];
            if (stageToStepMapping[nextStage] !== undefined) {
                if (delta === -1 && nextStage === STAGE.SUBMIT_APPLICATION) {
                    // we want to return to the start of the cards application
                    return STAGE.EMBURSE_CARDS;
                }
                return nextStage;
            }
        }
        return undefined;
    }
    function getFirstStageOfNextStep() {
        var _a;
        let index = activeStep;
        while (index >= 0 && index < steps.length) {
            index = index + 1;
            const newStep = steps[index];
            const nextStage = (_a = newStep === null || newStep === void 0 ? void 0 : newStep.stages) === null || _a === void 0 ? void 0 : _a[0];
            if (nextStage !== undefined) {
                return nextStage;
            }
        }
        return undefined;
    }
    function goNext() {
        continueFlow('next');
    }
    function goBack() {
        continueFlow('back');
    }
    function goSkip() {
        continueFlow('skip');
    }
    function continueFlow(direction = 'next') {
        let newStage;
        if (direction === 'skip') {
            newStage = getFirstStageOfNextStep();
        }
        else {
            newStage = getNextStage(direction);
        }
        if (newStage === undefined) {
            newStage = STAGE.FINISH;
        }
        const pendoEvent = PENDO_MAPPING[newStage];
        if (pendoEvent) {
            PendoService.trackEvent(pendoEvent);
        }
        goToStage(newStage, direction === 'next');
    }
    function goToStage(stage, completedVal = false) {
        const nextStep = stageToStepMapping[stage];
        // we are changing steps, we need to make the correct updates to completed and activeStep
        if (nextStep !== activeStep) {
            let start = Math.min(activeStep, nextStep);
            let end = Math.max(activeStep, nextStep);
            // we need to set all completed steps between the current and next step to false
            const newCompleted = {};
            for (let i = start; i < end; i++) {
                newCompleted[i] = completedVal;
            }
            // it's a representative and we are going to the final step (it's implied that we are skipping cards)
            if (isFromSales && nextStep === steps.length - 1 && skipReferralCards) {
                const cardsIndex = steps.findIndex((step) => step.id === STAGE.EMBURSE_CARDS);
                if (cardsIndex) {
                    newCompleted[cardsIndex] = false;
                }
            }
            setCompleted(Object.assign(Object.assign({}, completed), newCompleted));
            setActiveStep(nextStep);
        }
        if (stage === STAGE.FINISH) {
            setCompleted(Object.assign(Object.assign({}, completed), { [activeStep]: true }));
        }
        setCurrentStage(stage);
    }
    const onUserConfirmation = () => __awaiter(void 0, void 0, void 0, function* () {
        yield signupForEmburseCards(company, user);
        goNext();
    });
    const onEmburseCardSelection = () => __awaiter(void 0, void 0, void 0, function* () {
        return dispatch(new ShowModal(SignUpConfirmationModal, {
            user: user,
            company,
            onConfirm: () => onUserConfirmation()
        }));
    });
    function activateCompany() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield CompanyApi.activateCompany(company.id);
                GoogleAnalyticsService.trackEvent(GoogleEvents.CONVERSION, { send_to: 'AW-934154673/YhM_CMrbxKUaELGjuL0D' });
                window.location.href = '/';
            }
            catch (error) {
                toast.error('Error activating company', error);
            }
        });
    }
    function referralScreen() {
        return (_jsx(SignUpReferralScreen, { nextStep: goNext, activateCompany: activateCompany, company: company, applyForEmburseCards: applyForEmburseCards, onBack: () => goToStage(STAGE.PERSONAL_INFORMATION), isRsmPartner: isRsmPartner }));
    }
    // Map of stages to respective components. The next and previous stages are the functions above to navigate between stages
    const STAGES_MAP = {
        [STAGE.EMAIL_VERIFICATION]: _jsx(VerifyEmail, {}),
        [STAGE.COMPANY_INFORMATION]: (_jsx(FormProvider, Object.assign({}, companyFormMethods, { children: _jsx(CompanyInformationForm, { company: company, isAdditionalCompany: isAdditionalCompany, onCompanyInfoSubmitNewSignup: goNext }) }))),
        [STAGE.PERSONAL_INFORMATION]: _jsx(SignUpPersonalInfo, { onNext: goNext, onBack: goBack, showFlexSpacing: true }),
        [STAGE.PLAN_SELECTION]: (_jsx(SignUpPlanSelection, { onPlanSelection: goNext, onBack: goBack, channel: channel, onError: setGenericStripeError })),
        [STAGE.IMPLEMENTATION_OPTIONS]: (_jsx(SignUpImplementationSelection, { onImplementationSelection: goNext, onBack: goBack, onError: setGenericStripeError })),
        [STAGE.EMBURSE_CARDS]: _jsx(SignUpEmburseCards, { onEmburseCardSelection: onEmburseCardSelection, onBack: goBack, onSkip: goSkip }),
        [STAGE.BUSINESS_VERIFICATION]: (_jsx(GoToEmburse, { target: "_self", path: "/external-accounts", label: "", acsUrl: acsUrl, samlResponse: assertionResponse, autoClick: true })),
        [STAGE.REFERRAL_SCREEN]: referralScreen(),
        [STAGE.RSM_PARTNER_REFERRAL_SCREEN]: referralScreen(),
        [STAGE.PAYMENT_SUMMARY]: (_jsx(SignUpPaymentSummary, { company: company, emburseCards: applyForEmburseCards, onSummaryConfirmation: goNext, onError: setGenericStripeError })),
        [STAGE.FINISH]: _jsx(SignUpWelcomeScreen, { activateCompany: activateCompany })
    };
    let leftContent = null;
    if (currentStage === STAGE.EMAIL_VERIFICATION) {
        leftContent = (_jsx(Grid, Object.assign({ item: true, justifyContent: "center", className: "reviewsLeft" }, { children: _jsx(Grid, Object.assign({ item: true, container: true, style: { height: '100%' }, alignContent: "center" }, { children: _jsx(SignUpReviews, {}) })) })));
    }
    else if (![STAGE.PAYMENT_SUMMARY, STAGE.FINISH].includes(currentStage)) {
        leftContent = (_jsx(Grid, Object.assign({ item: true, justifyContent: "center", className: "reviewsLeft" }, { children: _jsx("div", Object.assign({ style: { height: '100%', alignItems: 'center', display: 'flex', flexDirection: 'column' } }, { children: _jsxs("div", Object.assign({ style: { alignContent: 'flex-start', marginTop: 106, minWidth: 250 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Getting Started" })), _jsx(Spacer, { size: 30 }), _jsx(Stepper, Object.assign({ orientation: "vertical", activeStep: activeStep }, { children: steps.map((step, index) => {
                                const labelProps = {};
                                if (!step.label || !step.content) {
                                    return null;
                                }
                                if (completed[index] === false && index < activeStep) {
                                    labelProps.optional = (_jsx(Typography, Object.assign({ variant: "caption", className: "skipped" }, { children: "Skipped" })));
                                }
                                else if (step.id === STAGE.EMBURSE_CARDS) {
                                    labelProps.optional = (_jsx(Typography, Object.assign({ variant: "caption", className: "skipped" }, { children: skipReferralCards ? 'Skipped' : 'Optional' })));
                                }
                                return (_jsxs(Step, Object.assign({ expanded: true, completed: completed[index] }, { children: [_jsx(StepLabel, Object.assign({}, labelProps, { children: step.label })), _jsx(StepContent, { children: step.content.map((content, stepIndex) => (_jsx(Typography, Object.assign({ style: { fontSize: '12px' } }, { children: content }), stepIndex))) })] }), index));
                            }) }))] })) })) })));
    }
    return (_jsx("div", Object.assign({ className: style.global, style: { height: '100%' } }, { children: _jsxs(Grid, Object.assign({ container: true, alignItems: "stretch", justifyContent: "center", style: { height: '100%' }, wrap: "nowrap" }, { children: [leftContent, _jsx(Grid, Object.assign({ container: true, item: true, className: "signUpRight", xs: true }, { children: _jsxs(Grid, Object.assign({ container: true, alignItems: "center", wrap: "nowrap", justifyContent: "center", direction: "column", sx: { height: '100%', overflowY: 'auto' } }, { children: [_jsx(Grid, Object.assign({ item: true, className: "maxWidth centerText" }, { children: _jsx("img", { className: "logo", src: logo, alt: "logo" }) })), apiError && (
                            /* 0 height to not shift page content */ _jsxs("div", Object.assign({ style: { height: 0, overflow: 'visible' } }, { children: [_jsx(Spacer, { size: 32 }), _jsx(Alert, Object.assign({ severity: "error", onClose: () => setApiError(null) }, { children: apiError }))] }))), _jsx(Grid, Object.assign({ item: true, sx: { height: '100%' } }, { children: STAGES_MAP[currentStage] }))] })) }))] })) })));
};
